/**
 * Anexo no editor: escolher, conferir e desistir (H16, §8.2).
 *
 * ── A regra é a MESMA do servidor, e vem pronta ───────────────────────────
 *
 * `conferirAnexos` mora em `@cliqcrm/contracts` e roda nos dois lados. Aqui ela
 * serve para recusar ANTES de subir — a H16 diz por quê: *"é a diferença entre
 * a pessoa saber na hora e esperar trinta segundos de upload para ouvir não"*.
 *
 * Recusar no navegador não substitui a validação do servidor e não é
 * redundância: são dois momentos diferentes do mesmo não. O que seria erro é
 * escrever a regra duas vezes — e é justamente isso que não acontece aqui.
 *
 * ── Por que a lista some da conferência quando é nota interna ─────────────
 *
 * A nota não passa pelo canal. Recusar um PDF de 6 MB porque o WhatsApp limita
 * imagem a 5 seria aplicar a regra de um caminho que o arquivo não vai
 * percorrer — e o servidor concorda: `enviar-mensagem.ts` pula a conferência
 * de canal quando `privada` é verdadeiro.
 */

import { conferirAnexos, type LimitesDeAnexo, type RecusaDeAnexo } from '@cliqcrm/contracts';

/**
 * O que o anexo é, do ponto de vista da PRÉVIA.
 *
 * Vem do MIME, e não da extensão: o nome do arquivo é escolhido por quem o
 * criou e mente com frequência — `audio.mp3` gravado como vídeo, `foto.jpg`
 * que é um HEIC. O navegador lê o tipo real e é ele quem vai desenhar.
 */
export type EspecieDeAnexo = 'imagem' | 'video' | 'audio' | 'arquivo';

/** Um arquivo escolhido, com o que a tela precisa para mostrá-lo. */
export interface AnexoEscolhido {
  /** Identidade na tela: dois arquivos podem ter o mesmo nome. */
  readonly id: string;
  readonly arquivo: File;
  readonly especie: EspecieDeAnexo;
  /**
   * `blob:` para imagem, vídeo e áudio; `null` para o resto.
   *
   * Criada uma vez e guardada, e não gerada na renderização: `createObjectURL`
   * reserva memória até alguém revogar, e chamá-la a cada render vazaria uma
   * URL por quadro enquanto a pessoa digita.
   */
  readonly previa: string | null;
}

export function especieDe(arquivo: File): EspecieDeAnexo {
  const tipo = (arquivo.type || '').toLowerCase();
  if (tipo.startsWith('image/')) return 'imagem';
  if (tipo.startsWith('video/')) return 'video';
  if (tipo.startsWith('audio/')) return 'audio';
  return 'arquivo';
}

function novoId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `anexo-${Date.now()}-${Math.random()}`;
}

/**
 * Mídia ganha prévia; o resto se apresenta por nome, tipo e tamanho.
 *
 * ── Por que vídeo e áudio também, e não só imagem ─────────────────────────
 *
 * Antes só imagem tinha prévia, e os outros dois viravam uma linha com o nome
 * do arquivo. Quem grava um áudio ou escolhe um vídeo mandava às cegas: não
 * dava para ouvir antes de enviar, nem conferir se o vídeo era o certo.
 *
 * E é um erro que não tem volta — a mensagem sai para o cliente. O áudio
 * gravado sem querer, o vídeo errado da galeria: os dois chegam do outro lado
 * antes de alguém notar.
 *
 * A prévia é local (`blob:`) e não custa rede: o arquivo já está na máquina.
 */
export function escolher(arquivo: File): AnexoEscolhido {
  const especie = especieDe(arquivo);
  return {
    id: novoId(),
    arquivo,
    especie,
    previa: especie === 'arquivo' ? null : URL.createObjectURL(arquivo),
  };
}

/**
 * Devolve a memória da prévia.
 *
 * Sem isto, cada imagem escolhida e removida fica retida até a aba fechar — e
 * numa tela de atendimento a aba fica aberta o dia inteiro.
 */
export function esquecer(anexo: AnexoEscolhido): void {
  if (anexo.previa) URL.revokeObjectURL(anexo.previa);
}

/**
 * A recusa, ou `null` quando o conjunto pode ser enviado.
 *
 * Recebe o conjunto INTEIRO, e não o arquivo novo: `quantidadeMaxima` só é
 * decidível olhando todos, e conferir só o que acabou de entrar deixaria
 * passar o décimo sexto arquivo de um canal que aceita quinze.
 */
export function recusaDe(
  anexos: readonly AnexoEscolhido[],
  limites: LimitesDeAnexo,
  canal: string | null,
  privada: boolean,
): RecusaDeAnexo | null {
  if (privada) return null;

  const paraConferir = anexos.map(({ arquivo }) => ({
    nome: arquivo.name,
    mime: arquivo.type,
    bytes: arquivo.size,
  }));

  return conferirAnexos(
    paraConferir,
    comAudioTolerado(paraConferir, limites),
    canal ?? 'este canal',
  );
}

/**
 * Os mesmos limites, com a regra de TIPO relaxada para áudio.
 *
 * ── Por que a tela precisa ser mais frouxa que o canal ────────────────────
 *
 * O Chrome grava em `audio/webm`, e o WhatsApp não aceita webm. O servidor sabe
 * disso e converte antes de enviar — `converter-audio.ts` existe exatamente
 * para isso, e converte todo áudio que o canal recusa POR TIPO.
 *
 * Só que a conferência do navegador roda antes do upload e usa a lista do
 * canal. O resultado, medido em 02/09/2026: gravar pelo botão de microfone do
 * próprio produto era recusado na hora, com "o canal não aceita audio/webm", e
 * o arquivo nunca chegava em quem o salvaria. A barreira que existe para
 * poupar upload estava barrando o caso que o upload resolveria.
 *
 * Relaxar SÓ o tipo, e só para áudio: tamanho e quantidade continuam valendo,
 * porque desses o servidor não salva ninguém. E relaxar acrescentando os tipos
 * presentes, em vez de pular a conferência, mantém uma regra só — a mesma
 * função, com uma lista maior.
 */
function comAudioTolerado(
  anexos: readonly { readonly mime: string }[],
  limites: LimitesDeAnexo,
): LimitesDeAnexo {
  // `null` já significa "o canal não tem regra de tipo": não há o que relaxar.
  if (limites.mimeAceitos === null) return limites;

  const audios = anexos
    .map((a) => a.mime.toLowerCase())
    .filter((mime) => mime.startsWith('audio/'));
  if (audios.length === 0) return limites;

  return { ...limites, mimeAceitos: [...limites.mimeAceitos, ...audios] };
}

/**
 * O canal aceita anexo?
 *
 * `quantidadeMaxima: 0` é o padrão conservador de `SEM_CAPACIDADES` — sem
 * capabilities, a tela não oferece o botão em vez de prometer o que não sabe.
 */
export function aceitaAnexo(limites: LimitesDeAnexo): boolean {
  return limites.quantidadeMaxima === null || limites.quantidadeMaxima > 0;
}

/**
 * O `accept` do seletor de arquivo.
 *
 * Filtra o que a janela do sistema oferece — não é barreira (dá para escolher
 * "todos os arquivos" em qualquer sistema), é conveniência: a pessoa não
 * navega até um `.mov` para descobrir que o canal não o aceita.
 *
 * Vazio quando o canal não tem regra, e aí a janela oferece tudo — que é a
 * verdade daquele canal.
 */
export function tiposParaOSeletor(limites: LimitesDeAnexo): string {
  return limites.mimeAceitos?.join(',') ?? '';
}

/** "2.4 MB", "812 KB" — o tamanho como a pessoa fala dele. */
export function tamanhoEmPalavras(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * O corpo do envio quando há arquivo.
 *
 * ── O booleano vai como texto, e de propósito ─────────────────────────────
 *
 * Todo valor de `FormData` vira string. `String(false)` é `"false"`, e do outro
 * lado `"false"` é uma string não vazia — verdadeira em qualquer coerção
 * automática. Se isso passasse, TODA mensagem com anexo viraria nota interna: o
 * vendedor anexa a foto, a tela mostra enviada, e o cliente nunca recebe.
 *
 * O servidor fecha essa porta em `anexo-da-requisicao.ts`, comparando com
 * `'true'` explicitamente. Aqui o valor é escrito no formato que aquela
 * comparação espera — os dois lados precisam concordar, e é por isso que a
 * conversão está documentada nos dois.
 */
export function corpoComAnexos(
  conteudo: string,
  privada: boolean,
  templateNome: string | undefined,
  anexos: readonly File[],
  mencoes: readonly string[] = [],
): FormData {
  const formulario = new FormData();
  formulario.append('conteudo', conteudo);
  formulario.append('privada', privada ? 'true' : 'false');
  if (templateNome) formulario.append('templateNome', templateNome);
  // Formulário não tem lista: as menções (#569) vão como JSON, e o servidor
  // as desfaz em `anexo-da-requisicao.ts`.
  if (mencoes.length > 0) formulario.append('mencoes', JSON.stringify(mencoes));
  for (const arquivo of anexos) formulario.append('anexos', arquivo, arquivo.name);
  return formulario;
}
